import type { ComponentProps } from "react";

import { Text } from "@radix-ui/themes";

import { cn } from "~/v2/utils/cn";
import "./livePrice.css";

/**
 * Which way a figure moved. `lift` is for figures that already carry a colour
 * of their own and only need to be brightened.
 */
export type PriceTick = "up" | "down" | "flat" | "lift";

/** The direction between two readings, for driving the flash. */
export const priceTick = (
  previous: number | undefined,
  next: number | undefined,
): PriceTick => {
  if (previous === undefined || next === undefined || previous === next) {
    return "flat";
  }
  return next > previous ? "up" : "down";
};

export interface LivePriceProps {
  /** Already-formatted price text. */
  children: string;
  tick: PriceTick;
  size?: "1" | "2" | "3";
  weight?: "regular" | "medium" | "bold";
  /** The flash paints the background, so a figure keeps its own text colour. */
  color?: ComponentProps<typeof Text>["color"];
}

/**
 * A price that briefly flashes green or red on change. Remount on each new
 * price (`key={price}`) so the CSS animation replays — it only runs on mount.
 */
export const LivePrice = ({
  children,
  tick,
  size = "2",
  weight = "medium",
  color,
}: LivePriceProps) => (
  <Text
    size={size}
    weight={weight}
    color={color}
    className={cn(
      "atd-numeric workspace-live-price",
      tick === "up" && "workspace-live-price--up",
      tick === "down" && "workspace-live-price--down",
      tick === "lift" && "workspace-live-price--lift",
    )}
  >
    {children}
  </Text>
);
